<script setup lang="ts">
import { ref, watch, nextTick, onMounted, onUnmounted, computed } from 'vue'
import { computePosition, flip, shift, offset, size, arrow, autoUpdate } from '@floating-ui/dom'
import { useCalloutStore } from '@/stores/calloutStore'

const props = defineProps<{
  id: string
  target: HTMLElement | null
  show: boolean,
  width?: string, // Made optional for safety
  arrow?: boolean // optionaler Pfeil, der auf das Ziel zeigt
}>()

const arrowRef = ref<HTMLElement | null>(null)
const arrowStyle = ref<Record<string, string>>({})

const emit = defineEmits<{ (e: 'close'): void }>()

const calloutRef = ref<HTMLElement | null>(null)
const coords = ref({ x: 0, y: 0 })
const availableHeight = ref<number | null>(null)
const availableWidth = ref<number | null>(null)
const calloutStore = useCalloutStore()
let cleanup: (() => void) | null = null

// Äußere Box: nur Position/Breite, KEIN overflow (sonst würde der Pfeil abgeschnitten).
const floatingStyles = computed(() => ({
  position: 'fixed' as const,
  top: `${coords.value.y}px`,
  left: `${coords.value.x}px`,
  minWidth: props.width || "200px",
  maxWidth: availableWidth.value ? `${availableWidth.value}px` : 'calc(100vw - 16px)',
}))

// Innerer Scroll-Container: Höhe begrenzen, vertikal scrollen, horizontal nie (Safari-Doppelscrollbalken).
const contentStyle = computed(() => ({
  maxHeight: (availableHeight.value && availableHeight.value < 450) ? `${availableHeight.value}px` : '90vh',
  overflowY: 'auto' as const,
  overflowX: 'hidden' as const,
}))

async function updatePosition() {
  if (!props.target || !calloutRef.value) return

  const middleware = [
    offset(8),
    flip(),
    shift({ padding: 8 }),
    size({
      padding: 8,
      apply({ availableHeight: ah, availableWidth: aw }) {
        availableHeight.value = ah
        availableWidth.value = aw
      },
    }),
  ]
  if (props.arrow && arrowRef.value) {
    middleware.push(arrow({ element: arrowRef.value, padding: 10 }))
  }

  const { x, y, placement, middlewareData } = await computePosition(props.target, calloutRef.value, {
    placement: 'top',
    strategy: 'fixed',
    middleware,
  })
  coords.value = { x, y }

  if (props.arrow && middlewareData.arrow) {
    const side = placement.split('-')[0]
    const staticSide = ({ top: 'bottom', right: 'left', bottom: 'top', left: 'right' } as Record<string, string>)[side]
    const { x: ax, y: ay } = middlewareData.arrow
    arrowStyle.value = {
      left: ax != null ? `${ax}px` : '',
      top: ay != null ? `${ay}px` : '',
      [staticSide]: '-7px',
    }
  }
}

watch(() => props.show, async (show) => {
  // Ensure previous listeners are gone
  if (cleanup) {
    cleanup()
    cleanup = null
  }

  if (show) {
    await nextTick()
    if (props.target && calloutRef.value) {
      // Floating UI's autoUpdate handles the initial call + listeners
      cleanup = autoUpdate(props.target, calloutRef.value, updatePosition)
      calloutStore.open(props.id)
    }
  } else {
    if (calloutStore.openId === props.id) {
      calloutStore.close(props.id)
    }
  }
}, { flush: 'post' }) // Ensures DOM is ready

function handleClickOutside(e: MouseEvent) {
  if (!props.show || !calloutRef.value) return

  const path = e.composedPath()
  const isClickInside = path.includes(calloutRef.value) || 
                       path.some(el => el instanceof HTMLElement && el.dataset.calloutPopup !== undefined)

  if (!isClickInside) {
    emit('close')
  }
}

onMounted(() => document.addEventListener('click', handleClickOutside))
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  if (cleanup) cleanup()
})
</script>

<template>
  <transition name="fade">
    <div
      v-if="show"
      ref="calloutRef"
      :style="floatingStyles"
      class="callout bg-white border rounded shadow-lg z-50"
    >
      <div
        v-if="arrow"
        ref="arrowRef"
        class="callout-arrow bg-white"
        :style="arrowStyle"
      ></div>
      <div
        class="callout-content relative bg-white rounded p-4"
        :style="contentStyle"
        style="touch-action: pan-y; -webkit-overflow-scrolling: touch;"
      >
        <slot />
      </div>
    </div>
  </transition>
</template>

<style scoped>
.callout-arrow {
  position: absolute;
  width: 14px;
  height: 14px;
  transform: rotate(45deg);
  border: 1px solid #d1d5db; /* gray-300, passt zum Callout-Rahmen */
  /* KEIN z-index: der Pfeil steht im DOM VOR dem Inhalt, der (position:relative,
     bg-white) darüber gemalt wird und die innere Hälfte überdeckt – so bleibt
     nur die äußere Dreieckshälfte sichtbar. */
}
</style>